import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { faEnvelope, faKeyboard } from '@fortawesome/free-regular-svg-icons';
import { faArrowDown, faArrowLeft, faArrowRight, faCircle, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Duration, DateTime } from 'luxon';
import axios, { type AxiosResponse } from 'axios';

import { NavLinkButton } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { Download as AudioDownload } from '@admin/components/audio/download';
import { Listen as AudioListen } from '@admin/components/audio/listen';
import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { titleize } from '@admin/components/helpers/titleize';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { Spinner } from '@admin/components/spinner';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { accountURL, editLeadURL } from '@admin/config/routes';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useTitle } from '@admin/hooks/use_title';
import { History__Subject, Ticket__TicketableEnum } from '@admin/schema';

type Conversation = {
  state?: string;
  recording_id?: number;
  playback_url?: string;
  user?: {
    name?: string;
  };
};

type Voicemail = {
  playback_url?: string;
};

type Activity = {
  verb: string;
  message?: string;
  created_at: string;
};

type PhoneCall = {
  id: number;
  sid?: string;
  phone?: string;
  system_phone?: string;
  status: string;
  direction: string;
  start_at?: string;
  end_at?: string;
  created_at: string;
  duration?: number;
  reasons?: string[];
  lead?: {
    id?: number;
    account_id?: number;
  };
  account?: {
    id?: number;
  };
  conversations: Conversation[];
  voicemails: Voicemail[];
  activities: Activity[];
};

const LOAD_ERROR_MESSAGE = 'Unable to load phone call. Please try again.';

const formatDuration = (seconds?: number): string => {
  const number = Math.round(Number(seconds)) || 0;
  if (number <= 0) {
    return '--:--';
  }
  return Duration.fromObject({ seconds: number }).toFormat('hh:mm:ss');
};

const formatMediumDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

const iconForActivity = (verb: string) => {
  if (verb.indexOf('call:inbound') === 0) return faArrowRight;
  if (verb.indexOf('call:outbound') === 0) return faArrowLeft;
  if (verb.indexOf('call:ivr') === 0) return faKeyboard;
  if (verb.indexOf('call:voicemail') === 0) return faEnvelope;
  if (verb.indexOf('call:conversation') === 0) return faUser;
  if (verb.indexOf('call:ended') === 0) return faArrowDown;
  return faCircle;
};

const activityLabel = (verb: string): string => titleize(verb.split(':').slice(1).join(' '));

const activityOffset = (activityCreatedAt: string, callCreatedAt: string): string => {
  const activityTime = DateTime.fromISO(activityCreatedAt);
  const callTime = DateTime.fromISO(callCreatedAt);
  const seconds = Math.round(activityTime.diff(callTime, 'seconds').seconds);
  return formatDuration(seconds);
};

const usePhoneCallRequest = (phoneCallID: string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<PhoneCall>> | undefined>();

  useEffect(() => {
    setRequest(axios.get<PhoneCall>(`/phone_calls/${phoneCallID}.json`));
  }, [phoneCallID]);

  return useAxiosRequest(request);
};

export const PhoneCallsShow: React.FC = () => {
  useTitle('Phone Call');
  const { id } = useParams<{ id: string }>();
  const { loading, response, error } = usePhoneCallRequest(id);
  const phoneCall = response?.data;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {LOAD_ERROR_MESSAGE}
      </div>
    );
  }

  if (!phoneCall) {
    return null;
  }

  const accountID = phoneCall.lead?.account_id ?? phoneCall.account?.id;

  return (
    <>
      <Roles show={[UserRole.Admin, UserRole.L2Agent, UserRole.Manager]}>
        <div className="text-right mar-btm">
          <CreateTechSupportTicket
            ticketable={{
              id: phoneCall.id,
              type: Ticket__TicketableEnum.PhoneCall,
              displayName: `Phone Call with ${phoneCall.phone ?? ''}`,
            }}
          />
        </div>
      </Roles>

      <div className="row">
        <div className="col-sm-6">
          <Panel>
            <Panel.Header>
              <Panel.Title>Details</Panel.Title>
            </Panel.Header>
            <Panel.Body>
              <p>
                <strong>ID:</strong> <span>{phoneCall.id}</span>
              </p>
              <Roles show={[UserRole.Admin]}>
                <p>
                  <strong>Twilio SID:</strong> <span>{phoneCall.sid}</span>
                </p>
              </Roles>
              <p>
                <strong>Status:</strong> <span className="label label-default">{titleize(phoneCall.status)}</span>
              </p>
              <p>
                <strong>Phone:</strong> {phoneCall.phone ? <PhoneFormatter number={phoneCall.phone} /> : '-'}
              </p>
              <p>
                <strong>System phone:</strong>{' '}
                {phoneCall.system_phone ? <PhoneFormatter number={phoneCall.system_phone} /> : '-'}
              </p>
              <p>
                <strong>Direction:</strong> <span>{titleize(phoneCall.direction)}</span>
              </p>
              <p>
                {phoneCall.lead?.id && !phoneCall.lead.account_id && (
                  <>
                    <strong>Lead:</strong>{' '}
                    <NavLinkButton kind="default" to={editLeadURL({ id: phoneCall.lead.id })} className="underline">
                      {phoneCall.lead.id}
                    </NavLinkButton>
                  </>
                )}
                {accountID && (
                  <>
                    <strong>Account:</strong>{' '}
                    <NavLinkButton kind="default" to={accountURL({ id: accountID })} className="underline">
                      {accountID}
                    </NavLinkButton>
                  </>
                )}
                {!phoneCall.lead?.id && !accountID && <span>Employee</span>}
              </p>
              {phoneCall.reasons && phoneCall.reasons.length > 0 && (
                <p>
                  <strong>Call Reasons:</strong>
                  <ul>
                    {phoneCall.reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </p>
              )}
              <p>
                <strong>Start at:</strong> <span>{formatMediumDate(phoneCall.start_at)}</span>
              </p>
              <p>
                <strong>End at:</strong> <span>{formatMediumDate(phoneCall.end_at)}</span>
              </p>
            </Panel.Body>
          </Panel>

          {phoneCall.conversations.length > 0 && (
            <Panel>
              <Panel.Header>
                <Panel.Title>Conversations</Panel.Title>
              </Panel.Header>
              {phoneCall.conversations.map((conversation, index) => (
                <Panel.Body key={index}>
                  <p>
                    <strong>User:</strong> <span>{conversation.user?.name}</span>
                  </p>
                  {conversation.recording_id ? (
                    <p>
                      <strong>Recording:</strong>{' '}
                      {conversation.playback_url && (
                        <>
                          <AudioListen
                            src={conversation.playback_url}
                            pageID={String(phoneCall.id)}
                            agent={conversation.user?.name}
                            date={phoneCall.start_at}
                          />{' '}
                          <AudioDownload src={conversation.playback_url} />
                        </>
                      )}
                    </p>
                  ) : (
                    <p>
                      <strong>Action:</strong> <span>Call {conversation.state}</span>
                    </p>
                  )}
                </Panel.Body>
              ))}
            </Panel>
          )}

          {phoneCall.voicemails.length > 0 && (
            <Panel>
              <Panel.Header>
                <Panel.Title>Voicemail</Panel.Title>
              </Panel.Header>
              {phoneCall.voicemails.map((voicemail, index) => (
                <Panel.Body key={index}>
                  <p>
                    <strong>Recording:</strong>{' '}
                    {voicemail.playback_url && (
                      <>
                        <AudioListen
                          src={voicemail.playback_url}
                          pageID={String(phoneCall.id)}
                          date={phoneCall.start_at}
                        />{' '}
                        <AudioDownload src={voicemail.playback_url} />
                      </>
                    )}
                  </p>
                </Panel.Body>
              ))}
            </Panel>
          )}
        </div>

        <div className="col-sm-6">
          <Panel>
            <Panel.Header>
              <Panel.Title>Timeline</Panel.Title>
            </Panel.Header>
            <Panel.Body>
              <div className="timeline">
                <div className="timeline-header">
                  <div className="timeline-header-title bg-info">{formatDuration(phoneCall.duration)}</div>
                </div>
                {phoneCall.activities.map((activity) => (
                  <div className="timeline-entry" key={activity.verb + activity.created_at}>
                    <div className="timeline-stat">
                      <div className="timeline-icon bg-success">
                        <FontAwesomeIcon icon={iconForActivity(activity.verb)} size="lg" />
                      </div>
                      <div className="timeline-time">{activityOffset(activity.created_at, phoneCall.created_at)}</div>
                    </div>
                    <div className="timeline-label">
                      <h4 className="mar-no pad-btm">{activityLabel(activity.verb)}</h4>
                      {activity.verb !== 'call:conversation:recording' && <p>{activity.message}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <HistoriesPanel type={History__Subject.PhoneCall} id={String(phoneCall.id)} />
    </>
  );
};
